// 翻译系统核心功能

import { isString } from 'lodash';

export type TranslationKey = string;
export type TranslationParams = Record<string, string | number>;
export type TranslationValues = Record<string, any>;

// 翻译缓存
const translationCache = new Map<string, TranslationValues>();

/**
 * 动态加载翻译文件
 */
export const loadTranslations = async (languageCode: string): Promise<TranslationValues> => {
  // 检查缓存
  if (translationCache.has(languageCode)) {
    return translationCache.get(languageCode)!;
  }

  try {
    // 动态导入翻译文件
    const translations = await import(`../../locales/${languageCode}/index.json`);
    const translationData = translations.default || translations;

    // 缓存翻译数据
    translationCache.set(languageCode, translationData);
    return translationData;
  } catch (error) {
    console.warn(`Failed to load translations for ${languageCode}:`, error);

    // 如果不是英语，尝试加载英语作为fallback
    if (languageCode !== 'en') {
      return loadTranslations('en');
    }

    // 返回空对象作为最后的fallback
    return {};
  }
};

/**
 * 获取嵌套对象的值
 */
const getNestedValue = (obj: any, path: string): any => {
  // 首先尝试直接查找（处理包含点号的完整key）
  if (obj && obj.hasOwnProperty(path)) {
    return obj[path];
  }

  // 如果直接查找失败，尝试嵌套查找（为了向后兼容）
  return path?.split('.')?.reduce((current, key) => current?.[key], obj);
};

const getTranslationValue = (obj: TranslationValues, key: TranslationKey): any => {
  const translation = getNestedValue(obj, key);
  if (translation !== undefined) return translation;

  const lowerCaseKey = key.toLowerCase();
  if (lowerCaseKey !== key) {
    const lowerCaseTranslation = getNestedValue(obj, lowerCaseKey);
    if (lowerCaseTranslation !== undefined) return lowerCaseTranslation;
  }

  const upperCaseKey = key.toUpperCase();
  if (upperCaseKey === key) return undefined;

  return getNestedValue(obj, upperCaseKey);
};

/**
 * 格式化翻译文本，支持参数插值
 */
const formatTranslation = (template: string, params: TranslationParams = {}): string => {
  if (!isString(template)) return '';
  // 同时支持单花括号 {param} 和双花括号 {{param}} 格式
  return template.replace(/\{\{?(\w+)\}?\}/g, (match, key) => {
    return params[key]?.toString() || match;
  });
};

/**
 * 翻译函数
 */
export const translate = (
  key: TranslationKey,
  params: TranslationParams = {},
  translations: TranslationValues = {},
  fallbackLanguageTranslations: TranslationValues = {},
): string => {
  // 1. 尝试从当前语言翻译中获取：原始 key 优先，其次小写 key
  let translation = getTranslationValue(translations, key);

  // 2. 如果没有找到，尝试从fallback语言获取：同样原始 key 优先，其次小写 key
  if (translation === undefined && fallbackLanguageTranslations) {
    translation = getTranslationValue(fallbackLanguageTranslations, key);
  }

  // 3. 如果仍然没有找到，返回key本身作为fallback，但也进行参数插值
  if (translation === undefined) {
    // console.warn(`Translation missing for key: ${key}`);
    return formatTranslation(key, params);
  }

  // 4. 如果是字符串，进行参数插值
  if (typeof translation === 'string') {
    return formatTranslation(translation, params);
  }

  // 5. 如果不是字符串，转换为字符串
  return String(translation);
};

/**
 * 批量翻译
 */
export const translateBatch = (
  keys: TranslationKey[],
  params: TranslationParams = {},
  translations: TranslationValues = {},
  fallbackLanguageTranslations: TranslationValues = {},
): Record<TranslationKey, string> => {
  const result: Record<TranslationKey, string> = {};

  keys.forEach((key) => {
    result[key] = translate(key, params, translations, fallbackLanguageTranslations);
  });

  return result;
};

/**
 * 清除翻译缓存
 */
export const clearTranslationCache = (): void => {
  translationCache.clear();
};

/**
 * 获取缓存的翻译语言列表
 */
export const getCachedLanguages = (): string[] => {
  return Array.from(translationCache.keys());
};

/**
 * 预加载翻译文件
 */
export const preloadTranslations = async (languageCodes: string[]): Promise<void> => {
  const promises = languageCodes.map((code) => loadTranslations(code));
  await Promise.all(promises);
};
